/*
 * Project: bellcloud
 * File: packages/types/device/lite/protocol_fn_configuration.ts
 * Author: SWAN
 * Created: 2025-07-08 11:46:48
 * Description:ClassID.CONFIGURATION (0x03)에 대한 FunctionID별 파라미터 정의
 */

import type { FunctionParameter } from '../fns';

export const params_configuration: Record<number, FunctionParameter[]> = {
  0x01: [],
  0x02: [
    {
      name: 'stealth_mode',
      type: 'u8',
      bytes: 1,
      comment: '0: off, 1: on',
      format: 'bool',
    },
    {
      name: 'safe_mode',
      type: 'u8',
      bytes: 1,
      comment: '0: off, 1: on',
      format: 'bool',
    },
    {
      name: 'latching_alarm',
      type: 'u8',
      bytes: 1,
      comment: '0: disabled, 1: enabled',
      format: 'bool',
    },
    {
      name: 'low_alarm_ack',
      type: 'u8',
      bytes: 1,
      comment: '0: disabled, 1: enabled',
      format: 'bool',
    },
    {
      name: 'confidence_beep',
      type: 'u8',
      bytes: 1,
      comment: '0: disabled, 1: enabled',
      format: 'bool',
    },
    {
      name: 'green_light',
      type: 'u8',
      bytes: 1,
      comment: '0: disabled, 1: enabled',
      format: 'bool',
    },
    { name: 'confidence_interval', type: 'u16', bytes: 2, comment: '1~60(sec)', format: 'number' },
    {
      name: 'intelliflash_interval',
      type: 'u16',
      bytes: 2,
      comment: '1~60(sec)',
      format: 'number',
    },
    {
      name: 'datalog_interval',
      type: 'u16',
      bytes: 2,
      comment: '1~60(sec)',
      format: 'number',
    },
    { name: 'worker_name', type: 'au8[]', bytes: 16, comment: 'Worker name', format: 'string' },

    { name: 'location_name', type: 'au8[]', bytes: 16, comment: 'Location name', format: 'string' },

    {
      name: 'timestamp',
      type: 'u32',
      bytes: 4,
      comment: 'seconds since 2023.01.01',
      format: 'date_seconds_from_basedate',
    },
    {
      name: 'calibration_gas_conc',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_conc',
    },
    {
      name: 'calibration_interval',
      type: 'au16[]',
      bytes: 10,
      comment: '0~365(days)',
      format: 'array_number',
    },
    {
      name: 'bump_interval',
      type: 'au16[]',
      bytes: 10,
      comment: '0~365(days)',
      format: 'array_number',
    },
    {
      name: 'low_alarm',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_conc',
    },
    {
      name: 'high_alarm',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_conc',
    },
    {
      name: 'twa_alarm',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_conc',
    },
    {
      name: 'stel_alarm',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_conc',
    },
    {
      name: 'stel_period',
      type: 'au8[]',
      bytes: 5,
      comment: '5 ~ 15 minutes, default: 15',
      format: 'array_number',
    },
    {
      name: 'twa_period',
      type: 'au8[]',
      bytes: 5,
      comment: '4 ~ 8 hours, default: 8',
      format: 'array_number',
    },

    {
      name: 'sensor_disabled',
      type: 'au8[]',
      bytes: 5,
      comment: '0: enabled(default), 1: disabled',
      format: 'array_bool',
    },
    {
      name: 'auto_fresh_air_cal',
      type: 'au8[]',
      bytes: 5,
      comment: '0: off(default), 1: on - DISABLED: 항상 off(0)로 설정됨 (2025-12-03)',
      format: 'array_bool',
    },
    {
      name: 'sensor_type',
      type: 'au8[]',
      bytes: 5,
      comment: 'refer to 0x0102 command',
      format: 'enum_array_sensors',
    },
    {
      name: 'gas_conc_unit',
      type: 'au8[]',
      bytes: 5,
      comment: '0: PPM, 1: %VOL, 2: %LEL',
      format: 'enum_array_gas_conc_unit',
    },
    {
      name: 'custom_lel',
      type: 'as16[]',
      bytes: 10,
      comment: '1000 means 1.0, LEL sensor only',
      format: 'array_conc',
    },
    {
      name: 'lel_CF_Index',
      type: 'au8[]',
      bytes: 5,
      comment: 'LEL sensor and CH4-IR sensor only',
      format: 'enum_array_cf_lel',
    },

    {
      name: 'set_date_time',
      type: 'u8',
      bytes: 1,
      comment: 'Reserved, ignore',
      format: 'bool',
    },
    {
      name: 'span_cal_and_bump',
      type: 'u8',
      bytes: 1,
      comment: '0: disabled, 1: enabled. For 5 gas detector',
      format: 'bool',
    },
    {
      name: 'language_setting',
      type: 'u8',
      bytes: 1,
      comment: '0: English, 1: Not English',
      format: 'language_setting',
    },
    {
      name: 'startup_message',
      type: 'au8[]',
      bytes: 16,
      comment: 'Startup message',
      format: 'startup_message',
    },
    {
      name: 'vol_for_50_lel_ch4',
      type: 'au8[]',
      bytes: 5,
      comment: 'LEL sensor and CH4-IR sensor only',
      format: 'enum_array_vol_for_50_lel_ch4',
    },
    {
      name: 'bump_test_threshold',
      type: 'au8[]',
      bytes: 5,
      comment: 'range: 40~100 (%)',
      format: 'array_number',
    },
  ],

  0x03: [
    {
      name: 'stealth_mode',
      type: 'u8',
      bytes: 1,
      comment: '0: off, 1: on',
      format: 'bool',
    },
    {
      name: 'safe_mode',
      type: 'u8',
      bytes: 1,
      comment: '0: off, 1: on',
      format: 'bool',
    },
    {
      name: 'latching_alarm',
      type: 'u8',
      bytes: 1,
      comment: '0: disabled, 1: enabled',
      format: 'bool',
    },
    {
      name: 'low_alarm_ack',
      type: 'u8',
      bytes: 1,
      comment: '0: disabled, 1: enabled',
      format: 'bool',
    },
    {
      name: 'confidence_beep',
      type: 'u8',
      bytes: 1,
      comment: '0: disabled, 1: enabled',
      format: 'bool',
    },
    {
      name: 'green_light',
      type: 'u8',
      bytes: 1,
      comment: '0: disabled, 1: enabled',
      format: 'bool',
    },
    { name: 'confidence_interval', type: 'u16', bytes: 2, comment: '1~60(sec)', format: 'number' },
    {
      name: 'intelliflash_interval',
      type: 'u16',
      bytes: 2,
      comment: '1~60(sec)',
      format: 'number',
    },
    {
      name: 'datalog_interval',
      type: 'u16',
      bytes: 2,
      comment: '1~60(sec)',
      format: 'number',
    },
    { name: 'worker_name', type: 'au8[]', bytes: 16, comment: 'Worker name', format: 'string' },

    { name: 'location_name', type: 'au8[]', bytes: 16, comment: 'Location name', format: 'string' },

    {
      name: 'timestamp',
      type: 'u32',
      bytes: 4,
      comment: 'seconds since 2023.01.01',
      format: 'date_seconds_from_basedate',
    },
    {
      name: 'calibration_gas_conc',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_conc',
    },
    {
      name: 'calibration_interval',
      type: 'au16[]',
      bytes: 10,
      comment: '0~365(days)',
      format: 'array_number',
    },
    {
      name: 'bump_interval',
      type: 'au16[]',
      bytes: 10,
      comment: '0~365(days)',
      format: 'array_number',
    },
    {
      name: 'low_alarm',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_conc',
    },
    {
      name: 'high_alarm',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_conc',
    },
    {
      name: 'twa_alarm',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_conc',
    },
    {
      name: 'stel_alarm',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_conc',
    },
    {
      name: 'stel_period',
      type: 'au8[]',
      bytes: 5,
      comment: '5 ~ 15 minutes, default: 15',
      format: 'array_number',
    },
    {
      name: 'twa_period',
      type: 'au8[]',
      bytes: 5,
      comment: '4 ~ 8 hours, default: 8',
      format: 'array_number',
    },

    {
      name: 'sensor_disabled',
      type: 'au8[]',
      bytes: 5,
      comment: '0: enabled(default), 1: disabled',
      format: 'array_bool',
    },
    {
      name: 'auto_fresh_air_cal',
      type: 'au8[]',
      bytes: 5,
      comment: '0: off(default), 1: on - DISABLED: 항상 off(0)로 설정됨 (2025-12-03)',
      format: 'array_bool',
    },
    {
      name: 'sensor_type',
      type: 'au8[]',
      bytes: 5,
      comment: 'refer to 0x0102 command',
      format: 'enum_array_sensors',
    },
    {
      name: 'gas_conc_unit',
      type: 'au8[]',
      bytes: 5,
      comment: '0: PPM, 1: %VOL, 2: %LEL',
      format: 'enum_array_gas_conc_unit',
    },
    {
      name: 'custom_lel',
      type: 'as16[]',
      bytes: 10,
      comment: '1000 means 1.0, LEL sensor only',
      format: 'array_conc',
    },
    {
      name: 'lel_CF_Index',
      type: 'au8[]',
      bytes: 5,
      comment: 'LEL sensor and CH4-IR sensor only',
      format: 'enum_array_cf_lel',
    },

    {
      name: 'set_date_time',
      type: 'u8',
      bytes: 1,
      comment: 'Reserved, ignore',
      format: 'bool',
    },
    {
      name: 'span_cal_and_bump',
      type: 'u8',
      bytes: 1,
      comment: '0: disabled, 1: enabled. For 5 gas detector',
      format: 'bool',
    },
    {
      name: 'language_setting',
      type: 'u8',
      bytes: 1,
      comment: '0: English, 1: Not English',
      format: 'language_setting',
    },
    {
      name: 'startup_message',
      type: 'au8[]',
      bytes: 16,
      comment: 'Startup message',
      format: 'startup_message',
    },
    {
      name: 'vol_for_50_lel_ch4',
      type: 'au8[]',
      bytes: 5,
      comment: 'LEL sensor and CH4-IR sensor only',
      format: 'enum_array_vol_for_50_lel_ch4',
    },
    {
      name: 'bump_test_threshold',
      type: 'au8[]',
      bytes: 5,
      comment: 'range: 40~100 (%)',
      format: 'array_number',
    },
  ],

  0x04: [
    {
      name: 'result',
      type: 'u8',
      bytes: 1,
      comment: '0: Fail, 1: Pass',
      format: 'number',
    },
  ],
};
